import { useEffect, useRef } from 'react';
import { mediaButtonService } from '../services/mediaButtonService';

/**
 * Custom hook for detecting media button presses from Bluetooth headsets.
 *
 * Uses a singleton MediaSession service - just registers/unregisters callbacks.
 * The service persists across component remounts to maintain media focus.
 *
 * @param {boolean} enabled - Whether media button detection is active
 * @param {function} onTrigger - Callback function to execute when button is pressed
 * @returns {object} - { isSupported: boolean }
 */
export const useMediaButton = (enabled, onTrigger) => {
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  // Handle enabled state changes
  useEffect(() => {
    console.log('[MediaButton] Hook state:', { enabled, isSupported: mediaButtonService.isSupported() });

    if (!enabled) {
      console.log('[MediaButton] Disabled - clearing callback');
      mediaButtonService.stopListening();
      return;
    }

    if (!mediaButtonService.isSupported()) {
      console.warn('[MediaButton] MediaSession API not supported');
      return;
    }

    // Register callback (service handles initialization)
    console.log('[MediaButton] Enabled - registering callback');
    mediaButtonService.startListening((action) => {
      console.log('[MediaButton] Hook received action:', action);
      onTriggerRef.current?.(action);
    });

    // Cleanup on unmount - stop listening when component unmounts
    return () => {
      console.log('[MediaButton] Unmounting - stopping listener');
      mediaButtonService.stopListening();
    };
  }, [enabled]);

  // Activate on user interaction (handles autoplay policy)
  useEffect(() => {
    if (!enabled) return;

    const activateOnInteraction = () => {
      console.log('[MediaButton] User interaction - activating');
      mediaButtonService.activate();
    };

    document.addEventListener('click', activateOnInteraction, { once: true });
    document.addEventListener('touchstart', activateOnInteraction, { once: true });

    return () => {
      document.removeEventListener('click', activateOnInteraction);
      document.removeEventListener('touchstart', activateOnInteraction);
    };
  }, [enabled]);

  return {
    isSupported: mediaButtonService.isSupported(),
  };
};
